import { Link, useLoaderData } from "@tanstack/react-router";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Clock, ChevronLeft } from "lucide-react";
import claraChat from "@/assets/clara-chat.jpg";
import BlogCTA from "@/components/blog/BlogCTA";
import RelatedPosts from "@/components/blog/RelatedPosts";
import { categoryColors } from "@/data/blog-posts";

const PageContent = () => {
  const { post } = useLoaderData({ from: "/blog/$slug" });
  const { open, setOpen } = useSchedule();

  const formattedDate = new Date(post.date + "T12:00:00").toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <>

      <div className="min-h-screen">
        <Navigation />

        <main>
          <article className="pt-28 sm:pt-32 pb-16 sm:pb-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              {/* Mobile back button + Breadcrumb */}
              <div className="mb-6 sm:mb-8">
                {/* Mobile: simple back link */}
                <Link 
                  to="/blog" 
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors sm:hidden active:scale-95"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Voltar ao blog
                </Link>
                {/* Desktop: full breadcrumb */}
                <nav aria-label="Breadcrumb" className="hidden sm:block">
                  <ol className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <li>
                      <Link to="/" className="hover:text-foreground transition-colors">Início</Link>
                    </li>
                    <li aria-hidden="true">›</li>
                    <li>
                      <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
                    </li>
                    <li aria-hidden="true">›</li>
                    <li className="text-foreground font-medium truncate max-w-none">
                      {post.title}
                    </li>
                  </ol>
                </nav>
              </div>

              {/* Header */}
              <header className="mb-6 sm:mb-12 space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[post.category]}`}>
                    {post.categoryLabel}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {post.readTime} de leitura
                  </span>
                </div>

                <h1 className="text-[1.6rem] leading-[1.2] sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground sm:leading-tight">
                  {post.title}
                </h1>

                <div className="flex items-center gap-3 pt-1 sm:pt-2">
                  <img
                    src={claraChat}
                    alt="Clara Camille"
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-primary/20"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground font-sans">Clara Camille</p>
                    <time dateTime={post.date} className="text-xs text-muted-foreground">
                      {formattedDate}
                    </time>
                  </div>
                </div>
              </header>

              {/* Divider */}
              <div className="w-12 h-0.5 bg-primary/20 rounded-full mb-6 sm:mb-8" />

              {/* Content */}
              <div className="prose-custom space-y-4 sm:space-y-5">
                {post.content.map((section, index) => {
                  switch (section.type) {
                    case "h2":
                      return (
                        <h2 key={index} className="text-lg sm:text-2xl font-typewriter font-bold text-foreground mt-8 sm:mt-10 mb-2 sm:mb-3 relative pl-4 border-l-[3px] border-primary/30">
                          {section.text}
                        </h2>
                      );
                    case "p":
                      return (
                        <p key={index} className="text-[0.95rem] sm:text-base text-muted-foreground font-sans leading-[1.75]">
                          {section.text}
                        </p>
                      );
                    case "list":
                      return (
                        <ul key={index} className="space-y-2.5 pl-1">
                          {section.items?.map((item, i) => (
                            <li key={i} className="text-[0.95rem] sm:text-base text-muted-foreground font-sans leading-[1.75] flex gap-2.5">
                              <span className="text-primary mt-0.5 flex-shrink-0">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    case "internal-link":
                      return (
                        <div key={index} className="my-5 sm:my-6 p-4 rounded-xl bg-primary/5 border border-primary/10 active:scale-[0.98] transition-transform">
                          <Link
                            to={section.linkHref || "/nutricionista-brasileira-na-australia"}
                            className="text-sm sm:text-base font-semibold text-primary hover:underline font-sans flex items-start gap-2"
                          >
                            <span className="flex-shrink-0">→</span>
                            <span>{section.linkText}</span>
                          </Link>
                        </div>
                      );
                    case "blog-link":
                      return (
                        <div key={index} className="my-4 p-3 sm:p-4 rounded-lg bg-card border border-border/50 active:scale-[0.98] transition-transform">
                          <Link
                            to={section.linkHref || "/blog"}
                            className="text-sm sm:text-base font-semibold text-primary hover:underline font-sans flex items-start gap-2"
                          >
                            <span className="flex-shrink-0">📖</span>
                            <span>{section.linkText}</span>
                          </Link>
                        </div>
                      );
                    default:
                      return null;
                  }
                })}
              </div>

              {/* CTA */}
              <BlogCTA />

              {/* Related Posts */}
              <RelatedPosts currentSlug={post.slug} relatedSlugs={post.relatedSlugs} />
            </div>
          </article>
        </main>

        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const BlogPost = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default BlogPost;
